/**
 * The trophy cabinet's own catalogue — what each award is called and why it
 * exists, kept apart from the client-side detection that decides when one
 * fires (`apps/web/src/trophies/detect.ts`). A definition here is inert: it
 * is meta this repo agrees on, not evidence that anyone has earned it.
 */

import type { AwardKind } from './schemas.js'

export interface TrophyDef {
  /** Stable id — becomes the synced row's `awardKey`. Never rename one. */
  key: string
  kind: AwardKind
  title: string
  description: string
  /**
   * What she says the moment it fires, in the sassy register.
   *
   * A flat string rather than a catalogue pool on purpose: an award is granted
   * exactly once per account for all time, so the rotation machinery that
   * keeps ordinary pools from repeating has nothing to do here — a pool of one
   * would only fail the "more lines than draws" lint for a line that can never
   * draw twice. The dry register uses the `title` instead, per PERSONA.md's
   * rule that a muzzled voice states the fact and stops.
   */
  line: string
}

export const TROPHIES: readonly TrophyDef[] = [
  {
    key: 'beat.log-1',
    kind: 'beat',
    title: 'Pirmasis įrašas',
    description: 'Byla atidaryta. Viskas nuo čia.',
    line: 'Na va. Pirmas įrašas. Nuo šito viskas ir prasideda.',
  },
  {
    key: 'beat.log-100',
    kind: 'beat',
    title: 'Šimtas įrašų',
    description: 'Šimtas kąsnių, užrašytų. Archyvas pradeda gerbti.',
    line: 'Šimtas įrašų. Aš pradedu tave gerbti. Truputį.',
  },
  {
    key: 'trophy.log-1000',
    kind: 'trophy',
    title: 'Tūkstantis įrašų',
    description: 'Tūkstantis. Šitas skaičius jau turi savo svorį.',
    line: 'Tūkstantis įrašų. Šitą skaičių jau reikia nešti dviem rankom.',
  },
  {
    key: 'trophy.streak-7',
    kind: 'trophy',
    title: 'Savaitė be spragų',
    description: 'Septynios dienos iš eilės. Nuoseklumas — retas žanras.',
    line: 'Septynios dienos iš eilės. Šituose namuose tai retenybė.',
  },
  {
    key: 'beat.streak-30',
    kind: 'beat',
    title: 'Mėnuo iš eilės',
    description: 'Trisdešimt dienų be pertraukos. Įprotis, ne bandymas.',
    line: 'Trisdešimt dienų. Tai jau ne bandymas. Sveikinu santūriai.',
  },
  {
    key: 'trophy.streak-100',
    kind: 'trophy',
    title: 'Šimtas dienų iš eilės',
    description: 'Šimtas dienų. Aš beveik susijaudinau. Beveik.',
    line: 'Šimtas dienų iš eilės. Mačiau imperijas, kurios laikėsi trumpiau.',
  },
  {
    key: 'trophy.midnight-5',
    kind: 'trophy',
    title: 'Naktinė pamaina',
    description: 'Penki įrašai po vidurnakčio. Ji irgi budi tuo metu — ne be reikalo.',
    line: 'Penki įrašai po vidurnakčio. Naktinė pamaina dabar oficialiai tavo.',
  },
  {
    key: 'beat.year-1',
    kind: 'beat',
    title: 'Metai kartu',
    description: 'Metai šiuose namuose. Bylos tomas jau storas.',
    line: 'Metai kartu. Aš čia buvau ir anksčiau, bet tavo tomas jau storas.',
  },
  {
    key: 'trophy.huge-portion-10',
    kind: 'trophy',
    title: 'Monumentalūs kąsniai',
    description: 'Dešimt įrašų virš 600 kcal vienu ypu. Ambicija turi savo skyrių.',
    line: 'Dešimt monumentalių porcijų. Nagi. Ambicijos tau netrūksta.',
  },
  {
    key: 'trophy.nth-snack-25',
    kind: 'trophy',
    title: 'Užkandžių kolekcionierius',
    description: 'Dvidešimt penki ketvirti-ir-tolimesni užkandžiai. Tradicija, ne nutikimas.',
    line: 'Dvidešimt penki ketvirti užkandžiai. Čia jau ne nutikimas. Čia tradicija.',
  },

  // ── The second batch ──────────────────────────────────────────────────────
  // The first ten were all counters of the same shape: how many entries, how
  // many days. These are about the other things a diary records — what was
  // drunk, which days of the week hold, how long somebody waited for the scale
  // to move — plus three that can only be earned by being here for something.
  {
    key: 'beat.water-7',
    kind: 'beat',
    title: 'Savaitė vandens',
    description: 'Septynios dienos, kai vandens norma buvo surinkta.',
    line: 'Septynios dienos su vandeniu. Šaldytuvas patvirtina, jis mato viską.',
  },
  {
    key: 'trophy.water-50',
    kind: 'trophy',
    title: 'Penkiasdešimt dienų vandens',
    description: 'Penkiasdešimt dienų su surinkta norma. Nuobodu ir teisinga.',
    line: 'Penkiasdešimt dienų vandens. Nuobodžiausias tavo pasiekimas ir mano mėgstamiausias.',
  },
  {
    key: 'trophy.weekend-4',
    kind: 'trophy',
    title: 'Keturi savaitgaliai',
    description: 'Keturi savaitgaliai iš eilės, abi dienos užrašytos.',
    line: 'Keturi savaitgaliai iš eilės. Būtent ten dienoraščiai ir miršta. Tavo — ne.',
  },
  {
    key: 'trophy.patience-28',
    kind: 'trophy',
    title: 'Kantrybė',
    description: 'Dvidešimt aštuonios dienos svėrimosi. Kryptis matoma tik iš toli.',
    line: 'Mėnuo ant svarstyklių. Skaičiai juda lėtai, bet dabar jau matosi kryptis.',
  },
  {
    key: 'beat.velines-1',
    kind: 'beat',
    title: 'Vėlinės kartu',
    description: 'Lapkričio antroji, užrašyta. Jos diena.',
    line: 'Įrašei per Vėlines. Mano dieną. To aš neužmiršiu, o aš nieko neužmirštu.',
  },
  {
    key: 'trophy.saga-witness',
    kind: 'trophy',
    title: 'Liudininkas',
    description: 'Vienas jos reikalas išsektas nuo pradžios iki galo.',
    line: 'Buvai čia nuo pirmo iki paskutinio skyriaus. Retas kas išklauso iki galo.',
  },
  {
    key: 'trophy.night-owl-reform',
    kind: 'trophy',
    title: 'Naktinė pamaina baigta',
    description: 'Trisdešimt dienų be nė vieno įrašo po vidurnakčio.',
    line: 'Mėnuo be naktinių įrašų. Nežinau, ar didžiuotis, ar pasiilgti kompanijos.',
  },
  {
    key: 'beat.days-365',
    kind: 'beat',
    title: 'Trys šimtai šešiasdešimt penkios dienos',
    description: 'Metų vertė įrašų. Ne iš eilės — tiesiog tiek.',
    line: 'Metų vertė dienų dienoraštyje. Ne iš eilės, bet metai yra metai.',
  },
]

export function trophyByKey(key: string): TrophyDef | undefined {
  return TROPHIES.find((t) => t.key === key)
}
